import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AppDatePicker,
  AppInput,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import { Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import RiskManagerSidebar from '../../../layout/screenSpecifics/Modules/InecoRiskManager/Sidebar/InecoRiskManagerSidebar';
import ModuleInecoRiskManagerBase from './ModuleInecoRiskManagerBase';

export default function ModuleInecoRiskManagerMilestoneScreen() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );

  const [loading, setLoading] = useState(true);
  const [milestone, setMilestone] = useState(null);
  const [showModalAddHistorical, setShowModalAddHistorical] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Introduce el nombre'),
    date: Yup.string().required('Selecciona una fecha'),
  });

  const loadMilestones = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_inecoRiskManager_risk_milestone_list"),
          {
            moduleBridgeHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setMilestone(data);
            dispatch(currentPageAction.change("milestone", data, true));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex]
  );

  const exportXls = async (values) => axios
    .post(
        axiosPath('api_r_user_module_inecoRiskManager_risk_xls_export'),
        {
            moduleBridgeHex,
            filters: 0,
            milestone: 1,
            ...values,
        }
    )
    .then(async () => {
        if (isMounted.current) {
        await loadMilestones();
        }
    })
    .catch((e) => dispatch(errorHandler(e)))
    .finally(() => setShowModalAddHistorical(false))

//   const exportXls = useCallback(
//     async (values) =>
//       axios
//         .post(axiosPath("api_r_user_module_inecoRiskManager_risk_xls_export"), {
//           moduleBridgeHex,
//           filters: 0,
//           milestone: 1,
//           ...values,
//         })
//         .then(async () => {
//           if (isMounted.current) {
//             await loadMilestones();
//           }
//         })
//         .catch((e) => dispatch(errorHandler(e)))
//         .finally(() => setShowModalAddHistorical(false)),
//     [moduleBridgeHex]
//   );

  const downloadMilestone = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_inecoRiskManager_risk_milestone_download"
          ),
          {
            moduleBridgeHex,
            ...values,
          }
        )
        .then(async ({ data }) => {
          if (isMounted.current) {
            window.location.href = data.s3FileUrl;
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalAddHistorical(false)),
    [moduleBridgeHex]
  );

  useEffect(() => {
    loadMilestones();
  }, []);

  return (
    <>
      <ModuleInecoRiskManagerBase />
      <RiskManagerSidebar activeRoute="user_module_inecoRiskManager_milestone_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [{ name: `${moduleData?.moduleName}` }]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <button
          type="button"
          onClick={() => setShowModalAddHistorical(true)}
          className="btn btn-sm btn-primary"
        >
          Nuevo histórico
        </button>
        {loading ? (
          <AppNoInfo minHeight={window.innerHeight - 60} />
        ) : (
          <div className="card mt-4 p-3 mb-4">
            <div className="row border-bottom mb-3 pb-3">
              <Header>
                <div className="col-lg-6 text-primary header">Nombre</div>
                <div className="col-lg-4 text-primary header d-flex flex justify-content-center">
                  Fecha
                </div>
                <div className="col-lg-2 text-primary header d-flex flex justify-content-center">
                  Descargar
                </div>
              </Header>
            </div>
            {milestone.map((m, index) => (
              <div key={index} className="row border-bottom mb-3 pb-3">
                <div className="col-lg-6">{m?.name}</div>
                <div className="col-lg-4 d-flex flex justify-content-center">
                  {m?.date}
                </div>
                <div className="col-lg-2 d-flex flex justify-content-center">
                  <span
                    type="button"
                    onClick={() =>
                      downloadMilestone({
                        milestoneID: m?.id,
                      })
                    }
                  >
                    <AppRemixIcon icon="download-2" filled />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <AppModal
        title="Nuevo histórico"
        show={showModalAddHistorical}
        onClose={() => setShowModalAddHistorical(false)}
      >
        <div>
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={{
              name: '',
              date: '',
            }}
            onSubmit={exportXls}
            validationSchema={validationSchema}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppInput
                  title="Nombre"
                  value={values.name}
                  onChange={(v) => setFieldValue('name', v)}
                  isClearable
                  error={errors.name}
                  required
                />
                <AppDatePicker
                  title="Fecha"
                  icon={<AppRemixIcon icon="calendar-check" />}
                  value={values.date}
                  onChange={(opt) => setFieldValue('date', opt)}
                  popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                  showMonthDropdown
                  showYearDropdown
                  dateFormatCalendar="MMMM"
                  isClearable
                  required
                  error={errors.date}
                  calendarStartDay={1}
                />
                <div className="d-flex flex justify-content-between mt-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setShowModalAddHistorical(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                  >
                    Crear histórico
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </AppModal>
    </>
  );
}

const Header = styled.div`
  font-weight: bold;
  display: flex;
  flex-grow: 1;
  font-size: 16px;
`;
